import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styles from '../badgeBase.module.scss';
import BadgeShieldCheck from '../BadgeShieldCheck';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import nextBadgeTooltipId from '../nextBadgeTooltipId';

const LABEL = 'Verified';
const SELLER_DESCRIPTION = 'This expert has been verified by Twine';
const CLIENT_DESCRIPTION = 'This client has verified their company on Twine.';

/**
 * Verified pill badge. Hidden when the user is Twine Recommends
 * (that badge supersedes Verified).
 */
const Verified = ({
  user,
  className = '',
  style,
  iconOnly = false,
  // InstantSearch browse cards use renderToStaticMarkup — OverlayTrigger
  // is stripped, so fall back to an inline CSS tooltip there.
  overlay = true,
  ...rest
}) => {
  const tooltipIdRef = useRef(null);
  const popoverIdRef = useRef(null);

  if (!user || !user.verified || user.twine_recommends) {
    return null;
  }

  // Ignore legacy img-only / title props still passed by older call sites
  // eslint-disable-next-line no-unused-vars
  const { overrideAvatar, alt, title, ...spanRest } = rest;

  const description =
    user.type === 'seller' ? SELLER_DESCRIPTION : CLIENT_DESCRIPTION;
  const showOverlay = Boolean(overlay);
  const showCssTooltip = !overlay;

  if (showCssTooltip && tooltipIdRef.current === null) {
    tooltipIdRef.current = nextBadgeTooltipId('verified-tooltip');
  }
  if (showOverlay && popoverIdRef.current === null) {
    popoverIdRef.current = nextBadgeTooltipId('verified-overlay');
  }
  const tooltipId = tooltipIdRef.current;
  const popoverId = popoverIdRef.current;

  const classNames = [
    styles.badge,
    styles.verified,
    iconOnly ? styles.iconOnly : null,
    showCssTooltip ? styles.withCssTooltip : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const badge = (
    <span
      className={classNames}
      style={style}
      tabIndex={showOverlay || showCssTooltip ? 0 : undefined}
      data-browse-status-badge={showCssTooltip ? '' : undefined}
      aria-label={iconOnly ? LABEL : undefined}
      aria-describedby={tooltipId || undefined}
      {...spanRest}
    >
      <BadgeShieldCheck variant="verified" />
      {iconOnly ? null : <span className={styles.label}>{LABEL}</span>}
      {showCssTooltip ? (
        <span id={tooltipId} className={styles.cssTooltip} role="tooltip">
          {description}
        </span>
      ) : null}
    </span>
  );

  if (showOverlay) {
    return (
      <OverlayTrigger
        placement="bottom"
        trigger={['hover', 'focus']}
        overlay={
          <Popover id={popoverId} placement="bottom">
            {description}
          </Popover>
        }
      >
        {badge}
      </OverlayTrigger>
    );
  }

  return badge;
};

Verified.propTypes = {
  user: PropTypes.shape({
    type: PropTypes.string,
    verified: PropTypes.bool,
    twine_recommends: PropTypes.bool,
  }),
  className: PropTypes.string,
  style: PropTypes.object,
  title: PropTypes.string,
  iconOnly: PropTypes.bool,
  overrideAvatar: PropTypes.bool,
  overlay: PropTypes.bool,
};

export default Verified;
